import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import api from '../services/api';
import './ItemImageUploader.css';

const ItemImageUploader = ({ itemId, onImagesUpdate }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const fileInputRef = useRef(null);

  const loadExistingImages = async () => {
    if (!itemId) return;
    setLoadingExisting(true);
    try {
      const response = await api.get(`/items/images/?item=${itemId}`);
      const imageList = response.data?.results || response.data || [];
      setExistingImages(Array.isArray(imageList) ? imageList : []);
    } catch (error) {
      console.error('Error loading existing images:', error);
      setExistingImages([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleDeleteExistingImage = async (imageId) => {
    if (!window.confirm(t('deleteConfirm') || 'Are you sure you want to delete this?')) {
      return;
    }

    try {
      await api.delete(`/items/images/${imageId}/`);

      setExistingImages((prev) => prev.filter((image) => image.id !== imageId));
      toast.success(t('deleted') || 'Deleted successfully');

      if (onImagesUpdate) {
        onImagesUpdate();
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error(t('error') || 'An error occurred');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file types
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} ${t('notAnImage') || 'is not an image'}`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`${file.name} ${t('fileTooLarge') || 'is too large (max 5MB)'}`);
        return false;
      }
      return true;
    });

    // Generate preview URLs
    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    
    setImages([...images, ...validFiles]);
    setPreviewUrls([...previewUrls, ...newPreviews]);
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(previewUrls[index]);
    setImages(images.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    if (images.length === 0) {
      toast.error(t('selectImages') || 'Please select at least one image');
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = images.map((file, index) => {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('item', itemId);
        formData.append('alt_text', file.name.split('.')[0]);
        formData.append('is_primary', index === 0); // First image is primary
        formData.append('order', index);

        return api.post(
          `/items/images/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      });

      await Promise.all(uploadPromises);

      toast.success(t('imagesUploaded') || 'Images uploaded successfully');
      
      // Clear form
      setImages([]);
      setPreviewUrls([]);
      setShowModal(false);
      
      // Refresh images
      if (onImagesUpdate) {
        onImagesUpdate();
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error(t('errorUploadingImages') || 'Error uploading images');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <button 
        type="button"
        className="upload-images-btn"
        onClick={async () => {
          setShowModal(true);
          await loadExistingImages();
        }}
        title={t('addImages') || 'Add Images'}
      >
        📷 {t('addImages') || 'Add Images'}
      </button>

      {showModal && (
        <div className="image-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>{t('uploadImages') || 'Upload Images'}</h3>
              <button 
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="image-modal-body">
              {/* Existing Images */}
              <div className="existing-images-section">
                <h4>{t('existingImages') || 'Existing Images'}</h4>
                {loadingExisting ? (
                  <p>{t('loading') || 'Loading'}...</p>
                ) : existingImages.length === 0 ? (
                  <p>{t('noImages') || 'No images'}</p>
                ) : (
                  <div className="preview-grid">
                    {existingImages.map((image) => (
                      <div key={image.id} className="preview-item">
                        <img
                          src={image.thumbnail_url || image.image_url}
                          alt={image.alt_text || 'Item image'}
                          onError={(e) => {
                            if (image.image_url && e.currentTarget.src !== image.image_url) {
                              e.currentTarget.src = image.image_url;
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={() => handleDeleteExistingImage(image.id)}
                          title={t('delete') || 'Delete'}
                        >
                          ✕
                        </button>
                        {image.is_primary && (
                          <span className="primary-badge">{t('primary')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* File Input Area */}
              <div 
                className="file-input-area"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleFileSelect({ target: { files: e.dataTransfer.files } });
                }}
              >
                <input 
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="browse-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  📁 {t('selectImages') || 'Select Images'}
                </button>
                <p>{t('dragAndDrop') || 'or drag and drop images here'}</p>
                <small>{t('imageLimits') || 'Max 5MB per image, PNG/JPG'}</small>
              </div>

              {/* Preview Grid */}
              {previewUrls.length > 0 && (
                <div className="preview-grid">
                  {previewUrls.map((url, index) => (
                    <div key={index} className="preview-item">
                      <img src={url} alt={`Preview ${index}`} />
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeImage(index)}
                      >
                        ✕
                      </button>
                      {index === 0 && (
                        <span className="primary-badge">{t('primary')}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="image-modal-footer">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setShowModal(false)}
              >
                {t('cancel')}
              </button>
              <button
                type="button"
                className="upload-btn"
                onClick={uploadImages}
                disabled={uploading || images.length === 0}
              >
                {uploading ? t('uploading') : t('upload')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ItemImageUploader;
